import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthStore from "../../stores/AuthStore";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User,
  Settings,
  LogOut,
  LayoutDashboard,
  MessageCircle,
  BookOpen,
  ShieldAlert,
  Shield,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/brand/logo";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useTranslation } from "react-i18next";

export function NavbarApp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = AuthStore.user;

  const logout = () => {
    queryClient.clear();
    AuthStore.logout();
    navigate("/");
  };

  const navItems = [
    { name: t("app.nav.dashboard"), path: "/dashboard", icon: LayoutDashboard },
    { name: t("app.nav.practice"), path: "/practice", icon: MessageCircle },
    { name: t("app.nav.redFlagCheck"), path: "/red-flag-check", icon: ShieldAlert },
    { name: t("app.nav.journal"), path: "/journal", icon: BookOpen },
    { name: t("app.nav.quiz"), path: "/quiz", icon: Sparkles },
    { name: t("app.nav.settings"), path: "/settings", icon: Settings },
  ];

  // Mobile bottom tab bar — 5 core destinations, app-style. Short labels.
  const tabItems = [
    { name: t("app.tab.home", { defaultValue: "Home" }), path: "/dashboard", icon: LayoutDashboard },
    { name: t("app.tab.practice", { defaultValue: "Practice" }), path: "/practice", icon: MessageCircle },
    { name: t("app.tab.redFlag", { defaultValue: "Red flag" }), path: "/red-flag-check", icon: ShieldAlert },
    { name: t("app.tab.journal", { defaultValue: "Journal" }), path: "/journal", icon: BookOpen },
    { name: t("app.nav.settings"), path: "/settings", icon: Settings },
  ];

  return (
    <>
    <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center">
              <Logo size={28} />
            </Link>

            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  className={cn(
                    "text-sm font-medium transition-colors",
                    location.pathname === item.path
                      ? "bg-accent text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  asChild
                >
                  <Link to={item.path}>
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.name}
                  </Link>
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            {AuthStore.isHavePermission("ADMIN") && (
              <Button variant="outline" size="sm" className="hidden md:flex" asChild>
                <Link to="/admin">
                  <Shield className="mr-2 h-4 w-4" />
                  {t("app.nav.adminPanel")}
                </Link>
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  <Avatar className="h-9 w-9 border-2 border-gray-100">
                    <AvatarImage src={user?.avatar} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {user?.name?.charAt(0).toUpperCase() || <User className="h-4 w-4" />}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    {t("app.nav.profile")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    {t("app.nav.settings")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("app.nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>

    {/* Mobile bottom tab bar — app-style */}
    <div className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex items-stretch justify-around h-16 pb-[env(safe-area-inset-bottom)]">
        {tabItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors",
                active ? "text-primary" : "text-muted-foreground"
              )}
            >
              <item.icon className={cn("h-5 w-5", active && "fill-primary/10")} />
              {item.name}
            </Link>
          );
        })}
      </div>
    </div>
    </>
  );
}
